import { IEvent } from '@/interfaces/event-log';
import { Env, ILocation, IMediaQuery } from 'interfaces/bridge';
import { CompleteStatus } from 'interfaces/socket';
import { action, observable } from 'mobx';
import socketStore from 'store/socket';
import { v4 as uuidv4 } from 'uuid';

declare global {
  interface Window {
    vkyc?: {
      __funProxy: <T, R extends any = null>(
        type: string,
        data?: R,
        callbackFuncId?: string,
      ) => Promise<T>;
    };
    [x: string]: (...arg: any) => any; // for callback
    bridge?: Bridge;
  }
}

class Bridge {
  @observable
  mediaQuery: IMediaQuery = { paddingTop: 0, paddingBottom: 0, density: 1 };

  static get env() {
    if (window.vkyc && typeof window.vkyc.__funProxy === 'function') {
      return Env.WEBVIEW;
    } else {
      return Env.H5;
    }
  }

  constructor() {
    this.initDeviceInfo();
  }

  @action
  async initDeviceInfo() {
    this.mediaQuery = (await this.deviceInfo()) ?? { paddingBottom: 0, paddingTop: 0, density: 1 };
  }

  startVideoCall() {
    switch (Bridge.env) {
      case Env.WEBVIEW:
        return window.vkyc?.__funProxy<null, any>('startVideoCall', '', '');
      default:
        return null;
    }
  }

  trackEvent(event: IEvent) {
    switch (Bridge.env) {
      case Env.WEBVIEW:
        window.vkyc?.__funProxy<null, any>('trackEvent', JSON.stringify(event), '');
        break;
      default:
        console.log('');
    }
  }

  startOutboundCall() {
    switch (Bridge.env) {
      case Env.WEBVIEW:
        return window.vkyc?.__funProxy<null, any>('startOutboundCall', '', '');
      default:
        return null;
    }
  }

  startPrecall() {
    switch (Bridge.env) {
      case Env.WEBVIEW:
        return window.vkyc?.__funProxy<null, any>('startPrecall', '', '');
      default:
        return null;
    }
  }

  getEnv() {
    return Bridge.env;
  }

  isWebView() {
    return Bridge.env === Env.WEBVIEW;
  }

  /**
   * get device info
   */
  async deviceInfo() {
    switch (Bridge.env) {
      case Env.WEBVIEW: {
        const data = await window.vkyc?.__funProxy<string, any>('device', '', '');
        if (data) {
          return Promise.resolve(JSON.parse(data) as IMediaQuery);
        } else {
          const result: IMediaQuery = {
            paddingTop: 0,
            paddingBottom: 0,
            density: 1,
          };
          return Promise.resolve(result);
        }
      }
      default: {
        const data: IMediaQuery = {
          paddingTop: 0,
          paddingBottom: 0,
          density: 1,
        };
        return Promise.resolve(data);
      }
    }
  }

  /**
   * open native camera and get the photo result
   * @returns base64 of photo
   */
  getPhoto() {
    const id = ('_' + uuidv4()).replace(/-/g, '_');
    console.log('init bridge take photo', id);
    return new Promise<string>((resolve, reject) => {
      window[id] = function(ifSuccess, data) {
        console.log('callback bridge take photo success', ifSuccess, data);
        resolve(data);
      };
      console.log('call bridge take photo');
      window.vkyc?.__funProxy<string, any>('takePhoto', '', id);
    });
  }

  /**
   * get the location info(lat, lng) from native
   */
  getLocation() {
    const id = ('_' + uuidv4()).replace(/-/g, '_');
    console.log('init bridge location', id);
    return new Promise<ILocation | null>((resolve, reject) => {
      window[id] = function(ifSuccess, data) {
        console.log('callback bridge location success', data);
        resolve(data);
      };
      console.log('call bridge location');
      window.vkyc?.__funProxy<string, any>('location', '', id);
    });
  }

  openUrl(url: string) {
    switch (Bridge.env) {
      case Env.WEBVIEW:
        console.log('call bridge: webview openExtBrowser');
        window.vkyc?.__funProxy<null, any>('openExtBrowser', url, '');
        break;
      default:
        window.open(url, '_blank');
        break;
    }
  }

  /**
   * close webview
   */
  back() {
    switch (Bridge.env) {
      case Env.WEBVIEW:
        return window.vkyc?.__funProxy<null, any>('back', '', '');
      default: {
        return null;
      }
    }
  }

  /**
   * close webview
   */
  closeSDK() {
    console.log('closeSDK');
    switch (Bridge.env) {
      case Env.WEBVIEW:
        return window.vkyc?.__funProxy<null, any>('closeSDK', '', '');
      default: {
        return null;
      }
    }
  }

  /**
   * for native use
   * @param status the status of complete: 'AGENTISSUE'|'MISSED'|'THANKYOU'|'SORRY'
   */
  toWebComplete(status: CompleteStatus) {
    // console.log('to web complete', status);
    socketStore.updateCompleteStatus(status);
    socketStore.goToComplete();
  }
}

if (!window.bridge) {
  window.bridge = new Bridge();
}

const bridge = window.bridge;
export default bridge;
